import { useCallback, useMemo, useState } from 'react';
import { useMap } from 'react-use';
import _groupBy from 'lodash/groupBy';

import useEvents from '@/modules/events/hooks/useEvents';
import {
  AssignmentTypeEnum,
  EventSelectionInput,
  EventUnion,
  RecurringTypeEnum,
} from '@/types/schema';

export const getAssignmentTypeMap = (eventSelections: EventSelectionInput[]) => {
  return eventSelections.reduce(
    (res, { eventId, assignmentType }) => {
      res[eventId] = assignmentType;
      return res;
    },
    {} as Record<string, AssignmentTypeEnum>,
  );
};

function useSessionSelection({ eventSelections }: { eventSelections: EventSelectionInput[] }) {
  const [_, { set, get, setAll }] = useMap<Record<string, AssignmentTypeEnum>>(
    getAssignmentTypeMap(eventSelections),
  );
  const [selectedItems, setSelectedItems] = useState<EventSelectionInput[]>(eventSelections);
  const selectedItemsSet = new Set(selectedItems);

  const parentEventIds = selectedItems
    .filter((item) => item.assignmentType === AssignmentTypeEnum.Specific)
    .map((item) => item.eventId);

  const { events } = useEvents({
    filters: {
      parentEventIds,
      recurringCategories: [RecurringTypeEnum.RecurringInstance],
    },
    pause: !parentEventIds.length,
  });

  const childSessionsMap = useMemo<Record<string, EventUnion[]>>(() => {
    if (!parentEventIds.length) return {};

    return _groupBy(events as EventUnion[], (event) => event.parentEventId);
  }, [events, parentEventIds]);

  const getAssignmentType = useCallback(
    (eventId: string, defaultAssignmentType = AssignmentTypeEnum.All) => {
      const assignmentType = get(eventId);
      if (!assignmentType) {
        set(eventId, defaultAssignmentType);
        return defaultAssignmentType;
      }
      return assignmentType;
    },
    [get, set],
  );

  const has = useCallback(
    (eventId: string) => {
      return selectedItems.findIndex((val) => val.eventId === eventId) !== -1;
    },
    [selectedItems],
  );

  const toggle = useCallback(
    (eventId: string, assignmentType?: AssignmentTypeEnum) => {
      if (assignmentType) {
        set(eventId, assignmentType);
        setSelectedItems((prevList) => {
          const itemIndex = prevList.findIndex((val) => val.eventId === eventId);
          if (itemIndex !== -1) {
            return prevList.map((listItem, i) => {
              if (i === itemIndex) {
                return { ...listItem, assignmentType };
              }
              return listItem;
            });
          }

          return prevList;
        });
      } else {
        setSelectedItems((prevList) => {
          const itemIndex = prevList.findIndex((val) => val.eventId === eventId);
          if (itemIndex === -1) {
            const assignmentType = getAssignmentType(eventId);
            const item = { eventId, assignmentType };
            return [...prevList, item];
          }

          return prevList.filter((_, i) => i !== itemIndex);
        });
      }
    },
    [getAssignmentType, set],
  );

  const reset = useCallback(() => {
    setAll(getAssignmentTypeMap(eventSelections));
    setSelectedItems([...eventSelections]);
  }, [eventSelections, setAll]);

  return { selectedItemsSet, has, toggle, reset, childSessionsMap, getAssignmentType };
}

export default useSessionSelection;
